import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Translate from 'modules/local/Translate'

import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {NullAsDefault, renderIf} from 'views/Shared'
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {GoDotFill} from "react-icons/go";
import {formatSimplifyDate} from "../../../../helpers/dateUtils";
import {DoubleValueItem} from "../../../../components/common/DoubleValueItem";
import {ItemInformation} from "../../../../components/common/ItemInformation";

export const renderCourseClass = ({translate: t, detail = true, renderContextMenu = NullAsDefault} = {}) => (item, index) => {
    const {
        course,
        to_date,
        from_date,
        description,
        coordinators = [],
        timetable_nexts = [],
        number_of_students,
    } = item ?? {}

    const {start_time, location_address} = timetable_nexts[0] ?? {}

    const {isSm} = useContext(LayoutContext)

    const renderNextLesson = (time, location) => (
        <span className="space-x-1">
                    <span style={{fontSize: 11}} className="font-bold text-green-600 capitalize tracking-wide">
                        {formatSimplifyDate(time, 'fullTime')}
                    </span>
            {renderIf(
                location,
                <React.Fragment>
                    <span className="text-xs text-color-500">{t('in')}</span>
                    <span style={{fontSize: 11}} className="font-semibold text-color-100 tracking-wide">
                                {location}
                            </span>
                </React.Fragment>
            )}
                </span>
    )

    return renderIf(
        item,
        <div key={index} className="p-4 custom-item-render transition-all duration-300 relative list-item">
            <h2>
                <Link
                    to={getLinkToDetail(item)}
                    className="font-bold text-lg text-color-000 no-underline">
                    {getTitle(item)}
                </Link>
            </h2>
            <div className="space-x-1 tracking-wider">
                {renderIf(
                    !isSm,
                    <span className="text-color-400">
                        {t('belong to the course')}
                    </span>
                )}
                <Link
                    to={getLinkToDetail(course)}
                    className="font-medium text-blue-500 no-underline">
                    {getTitle(course)}
                </Link>
            </div>
            <div className="my-2 border-b border-color-50"/>
            {renderIf(
                _.some([from_date, to_date]),
                <DoubleValueItem
                    showIcon={true}
                    labelFirst="from date"
                    valueFirst={formatSimplifyDate(from_date, '', 'DD/MM/YYYY')}
                    labelSecond={to_date ? '\u279D' : ''}
                    valueSecond={formatSimplifyDate(to_date, '', 'DD/MM/YYYY')}
                    className="italic tracking-wider"
                />
            )}
            {renderIf(
                coordinators.length > 0,
                <span className="space-x-1 leading-tight">
                    <GoDotFill size={10} className="text-color-400"/>
                    <span className="space-x-1 italic tracking-wider">
                        <span className="text-color-500">{`${t('class coordinator')} : `}</span>
                        {renderIf(
                            coordinators.length > 0,
                            coordinators.map((e, i) => (
                                <span key={i} className="font-semibold text-color-100">
                                    {e?.teacher?.prefix_title || ''} {e?.teacher_name} {i < coordinators.length - 1 && ', '}
                                </span>
                            ))
                        )}
                    </span>
                </span>
            )}
            {renderIf(
                start_time,
                <ItemInformation showIcon={true} label="the next lesson"
                                 value={renderNextLesson(start_time, location_address)}
                                 className="italic tracking-wider"/>
            )}
            {renderIf(
                detail && description,
                <div className="mt-1 max-lines-3 text-color-300 tracking-wider">
                    {description}
                </div>
            )}
            <div className="absolute right-5 top-5 contextIcon">
                {renderContextMenu(item)}
            </div>
        </div>
    )
}

export const renderMyCourseClass = ({renderContextMenu = NullAsDefault} = {}) => (item, index) => {

    const {isLg} = useContext(LayoutContext)
    const {
        course,
        to_date, from_date,
        coordinators = [],
        timetable_nexts = [],
        description,
    } = item ?? {}

    const {start_time, location_address} = timetable_nexts[0] ?? {}

    const renderNextLesson = (time, location) => (
        <Translate>
            {(t) => (
                <span className="space-x-1">
                    <span style={{fontSize: 11}} className="font-bold text-green-600 capitalize tracking-wide">
                        {formatSimplifyDate(time, 'fullTime')}
                    </span>
                    {renderIf(
                        location,
                        <React.Fragment>
                            <span className="text-xs text-color-500">{t('in')}</span>
                            <span style={{fontSize: 11}} className="font-semibold text-color-100 tracking-wide">
                                {location}
                            </span>
                        </React.Fragment>
                    )}
                </span>
            )}
        </Translate>
    )

    return renderIf(
        item,
        <Translate key={index}>
            {(t) => (
                <div
                    className="p-4 flex flex-col gap-1 background shadow-md hover:scale-2xs rounded-xl transition-all duration-300 relative">
                    <Link
                        to={getLinkToDetail(item)}
                        className="font-bold text-lg text-color-000 tracking-wide no-underline leading-snug">
                        {getTitle(item)}
                    </Link>
                    <div className="space-x-1 tracking-wider leading-snug">
                        <span className="text-color-400">
                            {t('belong to the course')}
                        </span>
                        <Link
                            to={getLinkToDetail(course)}
                            className="font-semibold text-blue-600 no-underline">
                            {getTitle(course)}
                        </Link>
                    </div>
                    <div className="my-1 border-b border-color-50"/>
                    {renderIf(
                        _.some([from_date, to_date]),
                        <span className="space-x-1 italic">
                            <span className="text-color-400">{t('time')}: </span>
                            <span className="font-semibold text-gray-800">
                                {formatSimplifyDate(from_date, 'medium')}
                                {to_date ? ` - ${formatSimplifyDate(to_date, 'medium')}` : ''}
                            </span>
                        </span>
                    )}
                    {renderIf(
                        coordinators.length > 0,
                        <span className="space-x-1 italic">
                            <span className="text-color-400">{`${t('class coordinator')} : `}</span>
                            {coordinators.map((e, i) => (
                                <span key={i} className="font-semibold text-gray-800">
                                    {e?.teacher?.prefix_title || ''} {e?.teacher_name} {i < coordinators.length - 1 && ', '}
                                </span>
                            ))}
                        </span>
                    )}
                    <div className="my-1 border-b border-color-50"/>
                    <div className="text-color-300 tracking-wider">
                        {description}
                    </div>
                    {timetable_nexts.length > 0 && (
                        <>
                            <div className="my-1 border-b border-color-50"/>
                            <ItemInformation
                                showIcon={true} label="the next lesson"
                                value={renderNextLesson(start_time, location_address)}
                                className="text-xs italic"
                            />
                        </>
                    )}
                </div>
            )}
        </Translate>
    )
}
